export default {
    list: {
        title: 'Manage Your Employee Meetings',
        description:
            'This page lets you schedule, review, and manage your thematic meetings with your employees.',
        prepareMeeting: 'Prepare a meeting',
        yourMeetings: 'Your meetings',
        searchPlaceholder: 'Search for an employee...',
        upcomingSection: 'Upcoming meetings',
        previousSection: 'Previous meetings',
        noUpcoming: 'No upcoming meetings.',
        noPrevious: 'No previous meetings.',
        noUpcomingFiltered: 'No upcoming meetings match the selected filters.',
        noPreviousFiltered: 'No previous meetings match the selected filters.',
        loadError: 'The meetings could not be loaded.',
        viewMeeting: 'View meeting',
        openMeeting: 'Open the meeting for {{name}}',
        meetingActions: 'Actions for the meeting with {{name}}',
        markCompleted: 'Mark as completed',
        markCompletedSuccess: 'Meeting marked as completed.',
        reopenMeeting: 'Reopen meeting',
        reopenMeetingSuccess: 'Meeting reopened.',
        deleteMeeting: 'Delete',
        deleteMeetingSuccess: 'Meeting deleted.',
        confirmDeleteTitle: 'Delete this meeting?',
        confirmDeleteMessage:
            'This action is irreversible. The context, agenda and preparation notes of this meeting will be permanently deleted. Its meeting notes stay in the employee’s Notes tab.',
        completedMeetings: 'Latest completed meetings',
        noCompletedMeetings: 'No completed meetings yet.',
        completedMeetingsError: 'Completed meetings could not be loaded.',
        overdueTag: 'Overdue',
        unknownTopic: 'Unknown topic',
    },
    topics: {
        skill: { label: 'Skill Development' },
        recurring: { label: 'Recurring 1 on 1 meeting' },
        goals: { label: 'Individual Goals' },
        feedback: { label: 'Feedback' },
        career: { label: 'Career Aspirations' },
        recognition: { label: 'Recognition' },
        workload: { label: 'Workload and Priority Management' },
        team: { label: 'Team Collaboration and Climate' },
        informal: { label: 'Informal meeting' },
    },
    status: {
        planned: 'Upcoming',
        completed: 'Completed',
    },
    stats: {
        title: 'Quick stats',
        planned: 'Upcoming meetings',
        thisMonth: 'This month',
        completed: 'Completed (12 months)',
        overdueLabel: 'Employees without a meeting for 3+ months',
        unavailable: 'Unavailable',
    },
    overdue: {
        dialogTitle: 'Employees with no meeting for 3+ months',
        lastMeetingOn: 'Last: {{date}}',
        noMeeting: 'No meeting yet',
        scheduleMeeting: 'Schedule a meeting with {{name}}',
        moreEmployees: 'Show more ({{count}} left)',
        moreEmployees_one: 'Show more ({{count}} left)',
        moreEmployees_other: 'Show more ({{count}} left)',
        searchPlaceholder: 'Search for an employee',
        noMatch: 'No employee matches this search',
        noEmployee: 'Every employee has had a meeting in the last 3 months',
        loadError: 'The employees could not be loaded',
    },
    calendar: {
        linksPending: 'Preparing the links to your calendar...',
        linksError:
            'The links to your calendar could not be prepared. You can still download the invitation.',
    },
    newMeeting: {
        title: 'Schedule Employee Meeting',
        stepEmployeeAndTopic: 'Employee and topic',
        stepContext: 'Context',
        stepsLabel: 'Preparation steps',
        stepStatus: {
            completed: 'completed',
            current: 'current',
            upcoming: 'upcoming',
        },
        stepProgress: 'Step {{current}} of {{total}} — {{label}}',
        employeeToMeet: 'Employee to meet',
        employeeSelected: 'Selected employee:',
        employeeNameUnavailable: 'name unavailable',
        changeEmployee: 'Change',
        changeEmployeeLabel: 'Change the selected employee',
        selectObjective: 'Select your objective for the meeting',
        topicsGroupLabel: 'Meeting objective',
        mostPopularTopics: 'Most popular topics',
        otherTopics: 'Other topics',
        topicsLoadError: 'The meeting topics could not be loaded for now.',
        topicsRetry: 'Try again',
        firstStepRequirements:
            'Select an employee, then a meeting topic, to move to the next step.',
        templateTitle: 'Select your template',
        templatesGroupLabel: 'Meeting template',
        templateStepRequirement: 'Select a template to move to the next step.',
        templatesEmpty: 'Your organization has no template for this topic.',
        templatesError: 'The templates could not be loaded. Try again later.',
        aiAssistantNotice:
            '<bold>Amelio AI Assistant</bold> will prepare the meeting plan for you. Describe what you would like to accomplish during this meeting or provide some context about the meeting with the employee.',
        contextLabel: 'Meeting context and objectives',
        contextPlaceholder: 'Type here...',
        contextHelp:
            'Saved with the meeting, shown and editable in its plan. Visible to you and your management line, never to the employee.',
        contextHelpAi:
            'Provide as much detail as possible to help the AI create a comprehensive meeting plan.',
        dateLabel: 'Date and time (optional)',
        datePlaceholder: 'Select a date',
        back: 'Back',
        nextStep: 'Next step',
        create: 'Generate meeting plan',
        creating: 'Generating the meeting plan...',
        createSuccess: 'Meeting created successfully.',
        createError: 'Failed to create the meeting.',
        topics: {
            skill: {
                description: 'Training opportunities and competency enhancement',
            },
            recurring: {
                description: 'Weekly, bi-weekly or monthly check-ins.',
            },
            goals: {
                description: 'Review and set personal objectives',
            },
            feedback: {
                description: 'Provide constructive feedback and receive input',
            },
            career: {
                description: 'Discuss future career goals and development path',
            },
            recognition: {
                description: 'Thanking for efforts, celebrating success, highlighting achievements',
            },
            workload: {
                description: 'Review current workload and prioritize tasks',
            },
            team: {
                description: 'Discuss team dynamics and working relationships',
            },
            informal: {
                description: 'Additional discussion points',
            },
        },
    },
    plan: {
        addTopic: 'Add topic',
        backToMeetings: 'Back to meetings',
        introTitle: 'Create a Meeting Plan',
        introDescription: 'Structured plan to guide your meeting and capture key insights.',
        selectedEmployee: 'You selected',
        context: 'Context:',
        editContext: 'Edit the context',
        contextPlaceholder: 'Add what this meeting should address',
        editTitle: 'Edit the title',
        titleField: 'Meeting title',
        titleRequired: 'The title cannot be empty.',
        titleSaveError: 'The title could not be saved. The previous one is back.',
        agendaGeneration: {
            generatingTitle: 'Writing your meeting plan',
            generatingMessage:
                "Amélio's AI is writing a new agenda from the topic, the context and the employee. This can take a moment.",
            unavailable: 'Agenda generation unavailable for now',
            retry: 'Try again',
        },
        tipsAccordionTitle: 'Tips for a successful meeting',
        templateTipsTitle: 'Tips for this topic',
        observationsTitle: 'Things to observe and follow up on',
        conversationTipsTitle: 'Tips for a meaningful conversation',
        preparationTitle: 'Your preparation before the meeting',
        notesLabel: 'Preparation notes',
        notesPlaceholder: 'Note your observations here...',
        saveNotes: 'Save',
        notesSaved: 'Notes saved.',
        notesEmptyCompleted:
            'You did not note any observations here before completing the meeting.',
        autosavedAt: 'Autosave: saved at {{time}}',
        autosaving: 'Saving...',
        autosaveFailed: 'Autosave failed',
        careerAspirationEmptyCompleted:
            'You did not fill in any answer here before completing the meeting.',
        careerAspirationLoadFailed:
            "This meeting's career aspirations could not be loaded. Reload the page to try again.",
        transcript: {
            title: 'What was said, gathered',
            description:
                "Import the text file of your meeting and Amélio's AI pulls out what matters: what was discussed, what was decided, what each of you committed to.",
            uploadPrompt: 'Import the meeting text to get a recap',
            chooseFile: 'Choose a file',
            recapTitle: 'Meeting recap',
            generatedBy: "Written by Amélio's AI",
            importAnother: 'Import another file',
            downloadRecap: 'Download the recap',
            uploadError:
                'The file could not be imported. Check its format and size, then try again.',
            uploadFileTooLarge: 'The file exceeds {{maxFileSizeMb}} MB.',
            recapError:
                'The recap could not be written. Your file is kept: try again, or import another one.',
            recapPending: 'Recap being prepared',
            recapUnavailable: 'Recap unavailable for now',
            disclosure:
                'Part of the meeting could not be taken into account; this recap covers only the exchanges that were available.',
            unusableTitle: 'No recap could be produced from this file.',
            unusableBody:
                'Notes, minutes and written summaries cannot be recapped, and neither can a transcript that is too short or that the employee does not appear in. Import the transcript of the meeting, with the names of the people speaking, to try again.',
            loadError: 'The transcript could not be loaded for now',
            retry: 'Try again',
            discussedPoints: 'Discussed points',
            decisions: 'Decisions',
            commitments: 'Commitments',
            followUps: 'Follow-ups',
            owner: {
                manager: '{{managerLabel}}',
                employee: 'Employee',
                both: 'Both',
                undetermined: 'Owner to confirm',
            },
        },
        deleteTopic: 'Delete the topic {{title}}',
        markCompleted: 'Mark meeting as completed',
        reopenMeeting: 'Reopen meeting',
        completedReadOnly: 'This meeting is completed: it is read-only.',
        errorLoadingMeeting: 'Error loading the meeting.',
        notScheduled: 'No date scheduled',
        meetingNotesTitle: 'Meeting notes',
        meetingNotesOpen: 'Open notes',
        meetingNotesClose: 'Close notes',
        meetingNotesEmpty: 'No notes for this meeting.',
        meetingNotesPlaceholder: 'Write your note…',
        meetingNotesDeleteTitle: 'Delete this note?',
        meetingNotesDeleteMessage:
            "The note will be removed from the employee's file. This cannot be undone.",
        meetingNotesLoadError: "This meeting's notes could not be loaded.",
        meetingNotesForbidden: "You don't have access to this employee's notes.",
        meetingNotesCreateError: 'The note could not be saved',
        meetingNotesUpdateError: 'The note could not be updated',
        meetingNotesDeleteError: 'The note could not be deleted',
        meetingNotesPageSummary: '{{start}} to {{end}} of {{total}}',
        agendaTitle: 'Meeting plan',
        agendaDescription:
            "Plan your meeting items and record the employee's answers during the meeting.",
        completionTitle: 'Ready to wrap up this meeting?',
        completionDescription:
            'Mark the meeting as completed to archive your notes and close out this plan.',
        completedTitle: 'Meeting completed',
        completedDescription:
            "You can reopen it to revisit your notes, adjust your plan or complete the employee's follow-up.",
        statusWithSchedule: '{{status}} · {{schedule}}',
        editDate: 'Change the meeting date ({{label}})',
        addToCalendar: 'Add to my calendar',
        addToOutlook: 'Add to Outlook',
        addToGoogle: 'Add to Google',
        downloadInvitation: 'Download the .ics file',
        calendarFileName: 'meeting-{{employee}}',
        calendarError: 'Failed to download the calendar.',
    },
    editMeeting: {
        dateTitle: 'Change the meeting date',
        cancel: 'Cancel',
        save: 'Save changes',
        saving: 'Saving...',
        updateSuccess: 'Meeting updated.',
        updateError: 'Failed to update the meeting.',
        conflictError: 'This meeting was completed and can no longer be edited.',
    },
    disabled: {
        title: 'Feature not enabled',
        description: 'This feature is not enabled for your organization.',
    },
    validation: {
        employeeRequired: 'Please select an employee.',
        topicRequired: 'Please select a topic.',
        contextMaxLength: 'Context must not exceed {{max}} characters.',
        contextTooMuchFormatting:
            'Context carries too much formatting to be saved. Remove some, or shorten the text.',
        notesMaxLength: 'Preparation notes must not exceed {{max}} characters.',
        notesTooMuchFormatting:
            'These notes carry too much formatting to be saved. Remove some, or shorten the text.',
    },
};
